import AsyncStorage from "@react-native-async-storage/async-storage";
import { Platform } from "react-native";

import { getLanguageLabel } from "@/constants/locales";
import {
  NotesListNotesByPlayerIdQuery,
  PlayerDetailPlayerMetaFragment,
} from "@/generated/graphql";

export const MAX_SUMMARY_LENGTH = 150;
export const CACHE_KEY_PREFIX = "ai-summary-";
export const SCOUTING_CACHE_KEY_PREFIX = "ai-scouting-summary-";
export const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Represents a cached AI-generated summary for a player, including the summary content,
 * the timestamp when it was generated, and the associated player ID.
 */
export interface AISummaryCache {
  content: string;
  timestamp: string;
  playerId: string;
}

/**
 * True when `language` names the same language as `label`, in either form
 * this pipeline passes: the English display label itself ("Spanish") or a
 * locale code or bare subtag. Codes route through the one locale source
 * rather than a local list of code spellings (SE-6505).
 * @param language - A display label, locale code, or bare subtag
 * @param label - The English display label to test against
 * @returns Whether the two name the same language
 */
const matchesLanguage = (language: string, label: string): boolean =>
  language === label || getLanguageLabel(language) === label;

/**
 * Resolve the club name to put in an AI-summary prompt.
 * @param playerData - Player fragment carrying the association and bio data.
 * @returns The club name, or `null` when no source has one.
 * @remarks
 * SE-7480: reads `team.name` -> `teamName` -> `bioData.currentClub`, the same
 * precedence the profile header uses (`playerPageHeaderUtils.formatCurrentTeam`)
 * and the same order as the canonical `selectClubName` in
 * `features/player-kanban/utils/playerPreviewModel`.
 *
 * The prompt builders previously read `bioData.currentClub` alone. That field is
 * provider-fed (materialized view behind a 24h org-scoped Redis cache with no
 * transfer-driven invalidation), so after a transfer it keeps returning the
 * player's PREVIOUS club — while `team`/`teamName`, which come from the
 * transactional association the header renders, are already correct on the very
 * same `player` object. That divergence is the reported defect: the summary
 * contradicted the header and Transfers panel on the same screen.
 *
 * Preferring the association can only ever be more current than the provider
 * field, so this narrows the failure to the case where every source is stale.
 * It does not close the backend half (`bioData.currentClub` is still served
 * stale to every other consumer) — that is tracked separately.
 */
export const resolvePromptClub = (
  playerData: PlayerDetailPlayerMetaFragment
): string | null =>
  playerData?.team?.name ??
  playerData?.teamName ??
  playerData?.bioData?.currentClub ??
  null;

/**
 * Check if language is Spanish variant
 * @param language - Language code or display label to check
 * @returns True if language is Spanish variant
 */
const isSpanish = (language: string): boolean =>
  matchesLanguage(language, "Spanish");

/**
 * Check if language is German variant
 * @param language - Language code or display label to check
 * @returns True if language is German variant
 */
const isGerman = (language: string): boolean =>
  matchesLanguage(language, "German");

/**
 * Get data from platform-specific storage
 * @param key - Storage key
 * @returns Stored data or null
 */
const getStorageItem = async (key: string): Promise<string | null> => {
  if (Platform.OS === "web") {
    return localStorage.getItem(key);
  }
  return AsyncStorage.getItem(key);
};

/**
 * Save data to platform-specific storage
 * @param key - Storage key
 * @param value - Data to store
 */
const setStorageItem = async (key: string, value: string): Promise<void> => {
  if (Platform.OS === "web") {
    localStorage.setItem(key, value);
    return;
  }
  await AsyncStorage.setItem(key, value);
};

/**
 * Translate status terms to the target language
 * @param status - The status term to translate
 * @param language - Target language code
 * @returns Translated status term
 */
export const translateStatus = (status: string, language: string): string => {
  if (isSpanish(language)) {
    const spanishTranslations: { [key: string]: string } = {
      "Currently injured": "Actualmente lesionado",
      "Active Scouting": "Exploración Activa",
      "Negotiation Phase": "Fase de Negociación",
      "Target Board": "Panel de Objetivos",
    };
    return spanishTranslations[status] || status;
  }

  if (isGerman(language)) {
    const germanTranslations: { [key: string]: string } = {
      "Currently injured": "Derzeit verletzt",
      "Active Scouting": "Aktives Scouting",
      "Negotiation Phase": "Verhandlungsphase",
      "Target Board": "Zieltafel",
    };
    return germanTranslations[status] || status;
  }

  return status;
};

/**
 * Clean AI-generated output to remove formatting artifacts
 * @param text - The raw AI-generated text to clean
 * @returns Cleaned and formatted summary text
 * @remarks *** DO NOT add word limit truncation - causes LLM errors (SE-3588) ***
 */
export const cleanSummaryOutput = (text: string): string => {
  if (!text) return "Summary generation failed.";

  // Remove any remaining artifacts - using non-backtracking patterns to prevent ReDoS
  return text
    .replace(/\[[^\]]{0,500}\]/g, "") // Remove brackets (bounded quantifier)
    .replace(/[*#_]/g, "") // Remove format chars
    .replace(/\n{2,}/g, " ") // Remove multiple line breaks (non-backtracking)
    .replace(/[ \t]+/g, " ") // Normalize spaces (non-backtracking)
    .trim();
};

/**
 * Generate fallback summary when AI generation fails
 * @param playerData - Player data fragment containing bio and position information
 * @returns Fallback summary text based on available player data
 */
export const generateFallbackSummary = (
  playerData: PlayerDetailPlayerMetaFragment
): string => {
  const playerName = playerData?.name ?? "";

  // Build fallback summary using immutable array operations
  const gprPart =
    playerName && playerData.bioData?.gpr
      ? [`${playerName} has a GPR of ${playerData.bioData.gpr.toFixed(1)}.`]
      : [];

  // SE-7480: prefer the transactional association over the provider-fed
  // `bioData.currentClub`, which goes stale after a transfer.
  const fallbackClub = resolvePromptClub(playerData);
  const clubPart = fallbackClub ? [`Currently plays for ${fallbackClub}.`] : [];

  const positions =
    playerData.positions && playerData.positions.length > 0
      ? playerData.positions
          .map(p => p?.name)
          .filter(Boolean)
          .join(", ")
      : "";

  const positionsPart = positions ? [`Plays as ${positions}.`] : [];

  const parts = [...gprPart, ...clubPart, ...positionsPart];

  return parts.length > 0 ? parts.join(" ") : "Player summary unavailable.";
};

/**
 * Format a single scout report into a readable string
 * @param report - Scout report data
 * @param report.scoutName - Name of the scout who submitted the report
 * @param report.overallScore - Overall rating score from the scout
 * @param report.qnaText - Detailed notes and observations from the scout
 * @returns Formatted scout report string
 */
const formatScoutReport = (report: {
  scoutName?: string | null;
  overallScore?: number | null;
  qnaText?: string | null;
}): string => {
  const scoutInfo = report.scoutName ? `[${report.scoutName}]` : "";
  const scoreInfo =
    report.overallScore !== null && report.overallScore !== undefined
      ? ` (Score: ${report.overallScore})`
      : "";
  const content = report.qnaText || "No notes";
  return `${scoutInfo}${scoreInfo}: ${content}`;
};

/**
 * Format all scout reports into a combined content string
 * @param scoutReports - Array of scout reports
 * @returns Formatted scout reports content string
 */
const formatScoutReportsContent = (
  scoutReports: PlayerDetailPlayerMetaFragment["scoutReports"]
): string =>
  scoutReports && scoutReports.length > 0
    ? scoutReports
        .filter((report): report is NonNullable<typeof report> => !!report)
        .map(formatScoutReport)
        .join(" | ")
    : "None";

/**
 * Format notes from the notes query into a prompt-friendly string
 * @param notesData - Notes query result from useNotesListNotesByPlayerIdQuery
 * @returns Formatted notes content string with author, date, and content
 */
export const formatNotesContent = (
  notesData: NotesListNotesByPlayerIdQuery | null | undefined
): string => {
  const edges = notesData?.listNotesByPlayerId?.edges;
  if (!edges || edges.length === 0) return "None";

  return edges
    .map(edge => {
      const authorName =
        edge.node.user?.cognitoUser?.Attributes?.find(
          attr => attr.Name === "name"
        )?.Value ?? "Unknown";
      const date = new Date(edge.node.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      const content = edge.node.body || edge.node.title;
      return `[${authorName}] (${date}): ${content}`;
    })
    .join(" | ");
};

/**
 * Builds the LLM prompt for generating an AI scouting brief from reports and notes.
 * Unlike generatePlayerPrompt, this focuses exclusively on scouting intelligence
 * (scout reports and notes) rather than metrics/valuation data.
 * @param playerData - Player fragment with bio, positions, and scouting data
 * @param notesData - Notes query result containing player notes
 * @param language - BCP-47 language code for localized output
 * @returns Formatted scouting prompt string ready for LLM consumption
 */
export const generateScoutingSummaryPrompt = (
  playerData: PlayerDetailPlayerMetaFragment,
  notesData: NotesListNotesByPlayerIdQuery | null | undefined,
  language: string = "en"
): string => {
  const playerName = playerData?.name ?? "";
  const languageName = getLanguageLabel(language);
  const scoutReportCount = playerData.scoutReports?.length || 0;
  const scoutReportsContent = formatScoutReportsContent(
    playerData.scoutReports
  );
  const notesContent = formatNotesContent(notesData);
  const notesCount = notesData?.listNotesByPlayerId?.edges?.length ?? 0;
  // SE-7480: see resolvePromptClub — `bioData.currentClub` alone is stale after
  // a transfer, which made the summary contradict the header on the same screen.
  const currentClub = resolvePromptClub(playerData) || "N/A";
  const positions = playerData.positions?.map(p => p?.name).join(", ") || "N/A";
  const age = playerData.bioData?.age || "N/A";

  return `Generate a concise football (soccer) scouting verdict in ${languageName}:

IMPORTANT: Base your summary ONLY on the scouting reports and notes provided below. Do NOT use general knowledge about any player.

Player: ${playerName || "Unknown"}, ${age}, ${positions}, ${currentClub}

SCOUTING REPORTS (${scoutReportCount}):
${scoutReportsContent}

NOTES (${notesCount}):
${notesContent}

INSTRUCTIONS:
1. Write a single cohesive paragraph that synthesizes the scouting intelligence into a clear verdict.
2. Focus on: key strengths, weaknesses, concerns, and any transfer intelligence (release clauses, availability) found in the notes.
3. If multiple scouts reported, weave in where they agree or disagree.
4. Do NOT use bullet points, numbered lists, or section headers. Write flowing prose only.
5. Keep it concise — aim for 3-5 sentences that give the reader a complete picture.
6. If no scouting reports exist but notes are available, summarize the notes and mention that no formal scouting reports have been submitted yet.
7. The summary MUST be about ${playerName || "Unknown"} who plays for ${currentClub}. Do NOT substitute, confuse, or reference any other player.

Write the scouting verdict now.`;
};

/**
 * Builds the LLM prompt for generating an AI scouting summary.
 * @param playerData - Player fragment with bio, positions, and scouting data
 * @param kanbanPhase - Optional kanban board phase for status context
 * @param language - BCP-47 language code for localized output
 * @returns Formatted prompt string ready for LLM consumption
 * @remarks
 * Includes anti-hallucination guardrails (SE-3763) that instruct the LLM to
 * use ONLY the provided player data and never substitute similar-named players.
 * Soccer/football domain framing (SE-3763 v2) prevents NON_SOCCER_PROMPT rejection
 * by embedding explicit soccer/football keywords throughout the prompt structure.
 * The language instruction MUST remain the first line for proper localization.
 */
/**
 * Format a contract expiration date as a human-readable months-remaining string.
 * @param contractExpires - ISO date string for contract expiration, if any
 * @returns "N/A" when missing, "Contract expired" once past, or "<N> months remaining"
 * @remarks Extracted from generatePlayerPrompt to keep cognitive complexity below the
 * sonarjs/cognitive-complexity threshold.
 */
const formatContractExpires = (
  contractExpires?: string | null | undefined
): string => {
  if (!contractExpires) return "N/A";
  const now = new Date();
  const expires = new Date(contractExpires);
  const monthsDiff = Math.round(
    (expires.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 30)
  );
  return monthsDiff > 0 ? `${monthsDiff} months remaining` : "Contract expired";
};

export const generatePlayerPrompt = (
  playerData: PlayerDetailPlayerMetaFragment,
  kanbanPhase?: { phaseName: string; boardName: string } | null,
  language: string = "en"
): string => {
  const playerName = playerData?.name ?? "";
  const scoutReportCount = playerData.scoutReports?.length || 0;
  const scoutReportsContent = formatScoutReportsContent(
    playerData.scoutReports
  );
  const languageName = getLanguageLabel(language);
  const injuryStatus = playerData.bioData?.isInjured
    ? translateStatus("Currently injured", languageName)
    : "";
  // Translate kanban phase name if present
  const translatedKanbanPhase = kanbanPhase
    ? {
        ...kanbanPhase,
        phaseName: translateStatus(kanbanPhase.phaseName, languageName),
      }
    : null;
  // Build GPR context warning for low scores
  const gprWarning =
    playerData.bioData?.gpr && playerData.bioData.gpr < 40
      ? `\nIMPORTANT: GPR ${playerData.bioData.gpr.toFixed(1)} is below 40 (below average quality). Use honest language like "limited" or "below standards". Avoid words like "solid", "promising", or "intriguing".`
      : "";
  // SE-7480: see resolvePromptClub — `bioData.currentClub` alone is stale after
  // a transfer, which made the summary contradict the header on the same screen.
  const currentClub = resolvePromptClub(playerData) || "N/A";
  const contractExpiresLabel = formatContractExpires(
    playerData.bioData?.contractExpires
  );
  console.log("📝 Generating prompt in", languageName, "for", playerName);

  // *** DO NOT CHANGE - Language MUST be at start of prompt for proper localization ***
  return `Generate a concise professional football (soccer) scouting summary in ${languageName}:

IMPORTANT: You MUST write about ONLY the player described in the data below. Do NOT substitute, confuse, or reference any other player, even if they share a similar name. Use ONLY the data provided in this prompt — do NOT use your general knowledge about any player.

Sport: Professional Football (Soccer)
Player: ${playerName || "Unknown"}
Age: ${playerData.bioData?.age || "N/A"}
Position: ${playerData.positions?.map(p => p?.name).join(", ") || "N/A"} (NON-NEGOTIABLE: MUST state projected football squad role like "likely backup", "third choice", "competing for starting position" based on GPR. Include this even if it exceeds word limit)
Football Club: ${currentClub}
Nationality: ${playerData.nationality?.name || "N/A"}
GPR: ${playerData.bioData?.gpr?.toFixed(1) || "N/A"} (Scale: 0-100 where 50 = average player in YOUR LEAGUE. Under 40 = below average for your league, 40-75 = average to above average, 75+ = good quality. Use only this GPR value - do NOT mention "Time Decay GPR" or "Seasonal GPM")
Team Fit: ${typeof playerData.bioData?.teamStyleFit === "number" ? playerData.bioData.teamStyleFit.toFixed(2) : playerData.bioData?.teamStyleFit || "N/A"} (How well player fits OUR TEAM's tactics, NOT their current team)
Player Fit: ${playerData.bioData?.fitScore ? Math.round(playerData.bioData.fitScore * 100) : "N/A"} (How well player matches OUR DEFINED ARCHETYPE for this role)
Scout Reports (${scoutReportCount}): ${scoutReportsContent}
Public Valuation: ${playerData.bioData?.playerValuation ? `€${playerData.bioData.playerValuation.toLocaleString()}` : "N/A"}
Lower Bound: ${playerData.bioData?.minGeminiPlayerValuation != null ? `€${playerData.bioData.minGeminiPlayerValuation.toLocaleString()}` : playerData.bioData?.fairFee != null ? `€${playerData.bioData.fairFee.toLocaleString()}` : "N/A"}
Upper Bound: ${playerData.bioData?.maxGeminiPlayerValuation != null ? `€${playerData.bioData.maxGeminiPlayerValuation.toLocaleString()}` : playerData.bioData?.expectedFee != null ? `€${playerData.bioData.expectedFee.toLocaleString()}` : "N/A"}
Contract Expires: ${contractExpiresLabel}
${translatedKanbanPhase ? `Status: ${translatedKanbanPhase.phaseName} (${translatedKanbanPhase.boardName})` : ""}
${injuryStatus ? `Injury: ${injuryStatus}` : ""}${gprWarning}

CRITICAL REQUIREMENTS:
1. You MUST include where the player would rank in our team's position group (e.g., "would be third-choice striker")
2. Position ranking is NON-NEGOTIABLE - always include this assessment
3. Base position ranking on GPR: <40 = reserve, 40-60 = backup, 60-75 = competing for starter, 75+ = likely starter
4. The summary MUST be about ${playerName || "Unknown"} who plays for ${currentClub}. If you find yourself writing about any other player or club, STOP and restart using only the data above.
5. Do NOT use your training data about any player. Use ONLY the data fields provided above.

Write a professional football scouting paragraph summarizing the player's profile.`;
};

const validateCache = (
  parsed: AISummaryCache,
  playerId: string
): AISummaryCache | null => {
  const cacheAge = Date.now() - new Date(parsed.timestamp).getTime();

  if (cacheAge < CACHE_TTL && parsed.playerId === playerId) {
    console.log("✅ Cache loaded - timestamp:", parsed.timestamp);
    console.log("✅ Cache age:", Math.floor(cacheAge / (1000 * 60)), "minutes");
    return parsed;
  }

  console.log("❌ Cache expired or different player");
  console.log(
    "Cache age:",
    Math.floor(cacheAge / (1000 * 60)),
    "minutes, TTL:",
    CACHE_TTL / (1000 * 60),
    "minutes"
  );
  return null;
};

export const loadCachedSummary = async (
  playerId: string,
  language: string = "en_US",
  cachePrefix: string = CACHE_KEY_PREFIX
): Promise<AISummaryCache | null> => {
  try {
    const cacheKey = `${cachePrefix}${playerId}-${language}`;
    const cachedData = await getStorageItem(cacheKey);

    if (cachedData) {
      const parsed: AISummaryCache = JSON.parse(cachedData);
      return validateCache(parsed, playerId);
    }
  } catch (err) {
    console.warn("Failed to load cached summary:", err);
  }
  return null;
};

export const saveSummaryToCache = async (
  playerId: string,
  content: string,
  language: string = "en_US",
  cachePrefix: string = CACHE_KEY_PREFIX
): Promise<string> => {
  try {
    const cacheKey = `${cachePrefix}${playerId}-${language}`;
    const timestamp = new Date().toISOString();
    const cacheData: AISummaryCache = {
      content,
      timestamp,
      playerId,
    };

    await setStorageItem(cacheKey, JSON.stringify(cacheData));
    console.log("💾 Cache saved with timestamp:", timestamp);
    return timestamp;
  } catch (err) {
    console.warn("Failed to cache summary:", err);
    return new Date().toISOString();
  }
};

const LAST_LANGUAGE_KEY_PREFIX = "ai-summary-last-lang-";

/** Which summary a stored record belongs to. Mirrors the cache namespaces. */
export type SummaryType = "player" | "scouting";

/**
 * Namespaces the last-generated-language record the way the summary cache is
 * already namespaced.
 *
 * The two records are read together: a remount reads the language here and then
 * looks for the cached summary under it. So if a scouting generation and a
 * player generation share one language record, a French scouting summary points
 * the player-summary card at a French player summary that was never generated,
 * and the card blanks. That is the same failure QA reported on SE-6506, reached
 * by a different route — it was merely unreachable while SE-6511 had the
 * profile outranking the restored value.
 *
 * `player` deliberately keeps the original un-suffixed key so records already
 * on users' devices keep working; only `scouting`, which never had a namespace
 * of its own to lose, moves.
 * @param playerId - Player the record belongs to
 * @param summaryType - Which summary produced it
 * @returns The storage key for that record
 */
const lastLanguageKey = (playerId: string, summaryType: SummaryType): string =>
  summaryType === "scouting"
    ? `${LAST_LANGUAGE_KEY_PREFIX}scouting-${playerId}`
    : `${LAST_LANGUAGE_KEY_PREFIX}${playerId}`;

/**
 * Persist the language used for the most recent AI summary generation
 * so it survives component remounts across tab navigation (SE-4074)
 * @param playerId - Player whose summary was generated
 * @param language - Language code used for generation
 * @param summaryType - Which summary was generated; namespaces the record
 */
export const saveLastGeneratedLanguage = async (
  playerId: string,
  language: string,
  summaryType: SummaryType = "player"
): Promise<void> => {
  try {
    await setStorageItem(lastLanguageKey(playerId, summaryType), language);
  } catch (err) {
    console.warn("Failed to save last generated language:", err);
  }
};

/**
 * Load the language used for the most recent AI summary generation
 * so the correct cached summary is displayed on remount (SE-4074)
 * @param playerId - Player whose summary language to load
 * @param summaryType - Which summary to read the record for
 * @returns Language code or null if none saved
 */
export const loadLastGeneratedLanguage = async (
  playerId: string,
  summaryType: SummaryType = "player"
): Promise<string | null> => {
  try {
    return await getStorageItem(lastLanguageKey(playerId, summaryType));
  } catch (err) {
    console.warn("Failed to load last generated language:", err);
    return null;
  }
};

export const formatLastUpdated = (
  lastUpdated: string | null
): string | null => {
  if (!lastUpdated) return null;

  try {
    const date = new Date(lastUpdated);
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60)
    );

    if (diffInMinutes < 1) {
      return "Just now";
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      // 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days}d ago`;
    }
  } catch (error) {
    console.warn("Error formatting timestamp:", error);
    return "Recently";
  }
};
